import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { api } from '@/api/client'
import type { Company, CompanyCategory, CompanyFilters, CompanyStatus, Market } from '@/api/types'
import { Button } from '@/components/ui/Button'
import {
  CompanyStatusBadge,
  EmptyState,
  ErrorBox,
  PageHeader,
  Pagination,
  PresenceBadges,
  Segmented,
  Select,
  SkeletonRows,
} from '@/components/ui/Primitives'
import { errorMessage, toast } from '@/components/ui/Toast'
import { CompanyFormModal } from '@/features/companies/CompanyFormModal'
import { ImportCompaniesModal } from '@/features/companies/ImportCompaniesModal'
import {
  CATEGORIES,
  CATEGORY_LABEL,
  COMPANY_STATUS_LABEL,
  MARKET_FLAG,
  formatNumber,
  hostOf,
  intervalLabel,
  relativeTime,
} from '@/lib/utils'
import { useUrlState } from '@/lib/useUrlState'

const PAGE_SIZE = 50

export function CompaniesPage() {
  const [adding, setAdding] = useState(false)
  const [importing, setImporting] = useState(false)
  const [filters, setFilters] = useUrlState({
    q: '',
    market: '',
    category: '',
    tier: '',
    status: '',
    industry: '',
    has_jobs: '',
    sort: 'name',
    page: '1',
  })
  const [search, setSearch] = useState(filters.q)
  useEffect(() => {
    const t = window.setTimeout(() => search !== filters.q && setFilters({ q: search }), 250)
    return () => window.clearTimeout(t)
  }, [search, filters.q, setFilters])

  const query: CompanyFilters = {
    q: filters.q,
    market: filters.market as Market | '',
    category: filters.category as CompanyCategory | '',
    tier: filters.tier ? Number(filters.tier) : '',
    status: filters.status as CompanyStatus | '',
    industry: filters.industry,
    has_jobs: filters.has_jobs as CompanyFilters['has_jobs'],
    sort: filters.sort as CompanyFilters['sort'],
    page: Number(filters.page) || 1,
    page_size: PAGE_SIZE,
  }
  const companies = useQuery({
    queryKey: ['companies', query],
    queryFn: () => api.companies(query),
    placeholderData: keepPreviousData,
    refetchInterval: (q) => (q.state.data?.items.some((c) => c.is_checking || c.status === 'pending') ? 5000 : 30_000),
  })
  const industries = useQuery({ queryKey: ['industries'], queryFn: api.industries, staleTime: 60_000 })

  const qc = useQueryClient()
  const checkFiltered = useMutation({
    mutationFn: () =>
      api.checkMany({
        market: query.market || undefined,
        category: query.category || undefined,
        tier: query.tier || undefined,
      }),
    onSuccess: (r) => {
      toast.success(`${formatNumber(r.companies)} companies queued (${formatNumber(r.career_pages_queued)} career pages)`)
      void qc.invalidateQueries({ queryKey: ['companies'] })
    },
    onError: (e) => toast.error(errorMessage(e)),
  })

  const data = companies.data
  const narrowed = Boolean(filters.market || filters.category || filters.tier)
  const anyFilter = Boolean(narrowed || filters.q || filters.status || filters.industry || filters.has_jobs)

  return (
    <>
      <PageHeader
        title="Companies"
        subtitle="The company list drives everything: each company's career pages are checked on its own schedule for new openings."
        actions={
          <>
            <Button variant="secondary" loading={checkFiltered.isPending} onClick={() => checkFiltered.mutate()}>
              Check {narrowed ? 'these' : 'all'} now
            </Button>
            <Button variant="secondary" onClick={() => setImporting(true)}>
              Import CSV
            </Button>
            <Button onClick={() => setAdding(true)}>Add company</Button>
          </>
        }
      />

      <div className="card overflow-hidden">
        <div className="space-y-2.5 border-b border-line px-4 py-3">
          <div className="flex flex-wrap items-center gap-2.5">
            <input
              className="field-box h-[38px] w-full sm:w-72"
              placeholder="Search companies…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Segmented
              value={filters.market}
              onChange={(market) => setFilters({ market })}
              options={[
                { value: '', label: 'All' },
                { value: 'IN', label: `${MARKET_FLAG.IN} India` },
                { value: 'US', label: `${MARKET_FLAG.US} USA` },
              ]}
            />
            <Segmented
              value={filters.category}
              onChange={(category) => setFilters({ category })}
              options={[{ value: '', label: 'Any type' }, ...CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABEL[c] }))]}
            />
            <Segmented
              value={filters.tier}
              onChange={(tier) => setFilters({ tier })}
              options={[
                { value: '', label: 'Any level' },
                { value: '1', label: 'L1' },
                { value: '2', label: 'L2' },
                { value: '3', label: 'L3' },
              ]}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Select value={filters.status} onChange={(e) => setFilters({ status: e.target.value })} className="w-auto">
              <option value="">Any status</option>
              {(Object.keys(COMPANY_STATUS_LABEL) as CompanyStatus[]).map((s) => (
                <option key={s} value={s}>
                  {COMPANY_STATUS_LABEL[s]}
                </option>
              ))}
            </Select>
            <Select value={filters.industry} onChange={(e) => setFilters({ industry: e.target.value })} className="w-auto">
              <option value="">Any industry</option>
              {industries.data?.map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </Select>
            <Select value={filters.has_jobs} onChange={(e) => setFilters({ has_jobs: e.target.value })} className="w-auto">
              <option value="">With or without jobs</option>
              <option value="true">Has open jobs</option>
              <option value="false">No open jobs</option>
            </Select>
            <Select value={filters.sort} onChange={(e) => setFilters({ sort: e.target.value })} className="ml-auto w-auto">
              <option value="name">Name A–Z</option>
              <option value="jobs">Most open jobs</option>
              <option value="newest_opening">Latest new opening</option>
              <option value="last_checked">Recently checked</option>
              <option value="next_check">Next to check</option>
              <option value="created">Recently added</option>
            </Select>
          </div>
        </div>

        {companies.isError && (
          <div className="p-4">
            <ErrorBox error={companies.error} onRetry={() => void companies.refetch()} />
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="table-base min-w-[1120px]">
            <thead>
              <tr>
                <th>Company</th>
                <th>India</th>
                <th>USA</th>
                <th className="text-right">Open jobs</th>
                <th className="text-right">New · 7d</th>
                <th>Last checked</th>
                <th>Last new opening</th>
                <th>Scheduler</th>
                <th className="w-[1%]" />
              </tr>
            </thead>
            <tbody className={companies.isPlaceholderData ? 'opacity-60' : ''}>
              {companies.isPending && <SkeletonRows cols={9} rows={10} />}
              {data?.items.map((company) => <CompanyRow key={company.id} company={company} />)}
            </tbody>
          </table>
        </div>
        {data && data.items.length === 0 && (
          <EmptyState
            title={anyFilter ? 'No companies match' : 'No companies yet'}
            body={anyFilter ? 'Try fewer filters.' : 'Add a company, or import your MNC / GCC list from a CSV.'}
            action={
              !anyFilter && (
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setImporting(true)}>
                    Import CSV
                  </Button>
                  <Button onClick={() => setAdding(true)}>Add company</Button>
                </div>
              )
            }
          />
        )}
        {data && data.total > 0 && (
          <Pagination page={data.page} pageSize={data.page_size} total={data.total} onPage={(p) => setFilters({ page: String(p) })} />
        )}
      </div>

      {adding && <CompanyFormModal onClose={() => setAdding(false)} />}
      {importing && <ImportCompaniesModal onClose={() => setImporting(false)} />}
    </>
  )
}

function CompanyRow({ company }: { company: Company }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const check = useMutation({
    mutationFn: () => api.checkCompany(company.id),
    onSuccess: () => {
      toast.success(`Checking ${company.name}`)
      void qc.invalidateQueries({ queryKey: ['companies'] })
    },
    onError: (e) => toast.error(errorMessage(e)),
  })
  const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()

  return (
    <tr className="cursor-pointer" onClick={() => navigate(`/companies/${company.id}`)}>
      <td className="max-w-[260px]">
        <Link to={`/companies/${company.id}`} className="block truncate font-medium hover:underline" onClick={stop}>
          {company.name}
        </Link>
        <p className="truncate text-[12.5px] text-ink-muted">
          {[company.industry, hostOf(company.website)].filter(Boolean).join(' · ') || '—'}
        </p>
      </td>
      <td>
        <PresenceBadges markets={company.markets} only="IN" />
      </td>
      <td>
        <PresenceBadges markets={company.markets} only="US" />
      </td>
      <td className="whitespace-nowrap text-right tabular-nums">
        {company.jobs_active ? (
          <Link to={`/jobs?company_id=${company.id}`} className="link" onClick={stop}>
            {formatNumber(company.jobs_active)}
          </Link>
        ) : (
          <span className="text-ink-faint">0</span>
        )}
        {company.jobs_active > 0 && (
          <span className="block text-[11.5px] text-ink-muted">
            {MARKET_FLAG.IN} {formatNumber(company.jobs_india)} · {MARKET_FLAG.US} {formatNumber(company.jobs_us)}
          </span>
        )}
      </td>
      <td className="text-right tabular-nums">
        {company.new_jobs_7d ? (
          <Link to={`/jobs?company_id=${company.id}&new_only=true&found_within_days=7&sort=recent`} className="font-medium text-[#1f7a4d] hover:underline" onClick={stop}>
            +{formatNumber(company.new_jobs_7d)}
          </Link>
        ) : (
          <span className="text-ink-faint">0</span>
        )}
      </td>
      <td className="whitespace-nowrap text-ink-soft">
        {relativeTime(company.last_checked_at)}
        {company.status === 'failing' && (
          <p className="max-w-[180px] truncate text-[12px] text-danger" title={company.last_error ?? ''}>
            {company.last_error}
          </p>
        )}
      </td>
      <td className="whitespace-nowrap text-ink-soft">{company.last_new_job_at ? relativeTime(company.last_new_job_at) : <span className="text-ink-faint">none yet</span>}</td>
      <td className="whitespace-nowrap">
        <CompanyStatusBadge status={company.status} checking={company.is_checking} />
        <p className="mt-0.5 text-[12px] text-ink-muted">
          {company.scheduler_enabled
            ? company.next_check_at
              ? `${intervalLabel(company.crawl_interval_hours).toLowerCase()} · next ${relativeTime(company.next_check_at)}`
              : intervalLabel(company.crawl_interval_hours).toLowerCase()
            : 'off'}
        </p>
      </td>
      <td onClick={stop}>
        <Button
          size="sm"
          variant="secondary"
          className="whitespace-nowrap"
          loading={check.isPending}
          disabled={company.is_checking || !company.scheduler_enabled || !company.sources_count}
          onClick={() => check.mutate()}
        >
          Check now
        </Button>
      </td>
    </tr>
  )
}
